/**
 * The component half of the route table: the page behind each declared path,
 * the guard chain wrapped around it, and the `<Switch>` the app renders.
 *
 * The declaration itself (paths, guards, chrome, sitemap) lives in
 * `lib/routes.ts`, which stays free of React so the build's sitemap script can
 * read it. `routeComponents` is keyed by that table's paths, so the two halves
 * cannot drift without failing to compile.
 */
import { SignIn, SignUp } from '@clerk/react'
import type { ComponentType, ReactNode } from 'react'
import { useEffect } from 'react'
import { Route, Switch, useLocation } from '#router'
import { useIsMobile } from '@/hooks/use-mobile'
import { resolveLegacyTopicRedirect } from '@/lib/problems/legacy-topic-redirects'
import {
  featureFlagOf,
  isFlagGuard,
  type NamedRouteGuard,
  type RouteGuard,
  type RoutePath,
  routes,
} from '@/lib/routes'
import About from './about'
import { Admin } from './admin'
import { AdminProtectedRoute } from './admin-protected-route'
import { ApplyRoute } from './audition/apply-route'
import Wizard from './audition/wizard'
import { ClientProtectedRoute } from './client-protected-route'
import CommunityGuidelines from './community-guidelines'
import CourseDetail from './course-detail'
import CourseList from './course-list'
import { Dashboard } from './dashboard/dashboard'
import { FeatureFlagRoute } from './feature-flag-route'
import Home from './home'
import HowItWorks from './how-it-works'
import { MarketingPageRoute } from './marketing-page-route'
import { MobileListDetail } from './mobile-list-detail'
import { MobileProblems } from './mobile-problems'
import {
  MobileChapterDetailPage,
  MobileSubtopicDetailPage,
  MobileTopicDetailPage,
} from './mobile-topic-chapter-detail-page'
import NotFound from './not-found'
import Pricing from './pricing'
import PrivacyPolicy from './privacy-policy'
import { ListDetailPage } from './problem-lists/list-detail-page'
import { ProblemListProvider } from './problem-lists/problem-list-context'
import ChapterDetailPage from './problems/chapter-detail-page'
import TopicDetailPage, {
  SubtopicDetailPage,
} from './problems/topic-detail-page'
import ProblemsTable from './problems-table'
import SecurityPolicy from './security-policy'
import { Settings } from './settings/settings'
import TermsOfService from './terms-of-service'
import ValidatedEditor from './validated-editor'

// Route handler components — defined at module scope so their identity
// is stable across renders (React uses function reference to determine
// component type; inline definitions cause unmount/remount on every
// parent re-render).

function Courses() {
  const location = useLocation()
  return location.course ? <CourseDetail /> : <CourseList />
}

function Problems() {
  const location = useLocation()
  const { navigate } = location
  const isMobile = useIsMobile()

  const topicMatch = location.pathname.match(/^\/problems\/topics\/([^/]+)$/)

  // Legacy topic slugs (pre-taxonomy) redirect to their new taxonomy home, so
  // old bookmarks and any not-yet-migrated internal links keep working
  // (LG-339 PR 6). The effect runs unconditionally; the target gates it.
  const legacyRedirect = topicMatch
    ? resolveLegacyTopicRedirect(topicMatch[1])
    : null
  useEffect(() => {
    if (legacyRedirect) navigate(legacyRedirect, { replace: true })
  }, [legacyRedirect, navigate])
  if (legacyRedirect) return null

  if (topicMatch) {
    return isMobile ? (
      <MobileTopicDetailPage key={topicMatch[1]} />
    ) : (
      <TopicDetailPage key={topicMatch[1]} />
    )
  }

  const subtopicMatch = location.pathname.match(
    /^\/problems\/subtopics\/([^/]+)$/,
  )
  if (subtopicMatch) {
    return isMobile ? (
      <MobileSubtopicDetailPage key={subtopicMatch[1]} />
    ) : (
      <SubtopicDetailPage key={subtopicMatch[1]} />
    )
  }

  const chapterMatch = location.pathname.match(/^\/problems\/book\/([^/]+)$/)
  if (chapterMatch) {
    return isMobile ? (
      <MobileChapterDetailPage key={chapterMatch[1]} />
    ) : (
      <ChapterDetailPage key={chapterMatch[1]} />
    )
  }

  const listMatch = location.pathname.match(/^\/problems\/lists\/([^/]+)$/)
  if (listMatch) {
    return (
      <ProblemListProvider>
        {isMobile ? (
          <MobileListDetail key={listMatch[1]} />
        ) : (
          <ListDetailPage key={listMatch[1]} />
        )}
      </ProblemListProvider>
    )
  }

  return isMobile ? <MobileProblems /> : <ProblemsTable />
}

function SignInRoute() {
  return (
    <SignIn
      routing="path"
      path="/sign-in"
      signUpUrl="/sign-up"
      fallbackRedirectUrl="/"
    />
  )
}

function SignUpRoute() {
  return (
    <SignUp
      routing="path"
      path="/sign-up"
      signInUrl="/sign-in"
      fallbackRedirectUrl="/"
    />
  )
}

/**
 * The page behind each declared path. `Record<RoutePath, …>` is the seam that
 * makes the split table safe: declare a route in lib/routes.ts and this fails
 * to compile until the page is bound, and vice versa.
 */
const routeComponents: Record<RoutePath, ComponentType> = {
  '/': Home,
  '/sign-in': SignInRoute,
  '/sign-up': SignUpRoute,
  '/dashboard': Dashboard,
  '/settings/:section?': Settings,
  '/courses/*?': Courses,
  '/problems/*?': Problems,
  '/editor/*?': ValidatedEditor,
  '/admin/*?': Admin,
  '/apply': Wizard,
  '/guidelines': CommunityGuidelines,
  '/about': About,
  '/how-it-works': HowItWorks,
  '/pricing': Pricing,
  '/terms': TermsOfService,
  '/privacy': PrivacyPolicy,
  '/security': SecurityPolicy,
}

const GUARD_COMPONENTS: Record<
  NamedRouteGuard,
  ComponentType<{ children: ReactNode }>
> = {
  admin: AdminProtectedRoute,
  client: ClientProtectedRoute,
  marketing: MarketingPageRoute,
  apply: ApplyRoute,
}

function wrapInGuard(guard: RouteGuard, children: ReactNode): ReactNode {
  if (isFlagGuard(guard)) {
    return (
      <FeatureFlagRoute flag={featureFlagOf(guard)}>
        {children}
      </FeatureFlagRoute>
    )
  }
  const Guard = GUARD_COMPONENTS[guard]
  return <Guard>{children}</Guard>
}

// Built once: the table is static, so the guard chains are too. `Switch`
// reads its children as a flat list and skips anything that isn't an element,
// so the 404 fallback joins the same array rather than sitting beside it.
const routeElements: ReactNode[] = [
  ...routes.map((route) => {
    const Page = routeComponents[route.path]
    const guards: readonly RouteGuard[] = route.guards ?? []
    return (
      <Route key={route.path} path={route.path}>
        {guards.reduceRight<ReactNode>(
          (child, guard) => wrapInGuard(guard, child),
          <Page />,
        )}
      </Route>
    )
  }),
  <Route key="not-found">{NotFound}</Route>,
]

/** Every top-level route, in the declared match order, plus the 404 fallback. */
export function RouteSwitch() {
  return <Switch>{routeElements}</Switch>
}
